// Display-only strings for the public /re/payment lookup page.
// Submitted payload values (paymentAmount, squarePaymentToken) and the
// registration `id` stay language-neutral — the backend is unaffected.

const en = {
  languageLabel: { en: 'English', es: 'Español' },

  // Lookup step
  pageTitle: 'Religious Education Payment',
  pageSubtitle: 'Enter the email address used at registration and select the school year you are paying for.',
  schoolYear: 'School Year',
  noSchoolYears: 'No school years configured',
  emailAddress: 'Email Address',
  lookUpRegistration: 'Look Up Registration',

  // Select step
  lookUpDifferentEmail: 'Look Up Different Email',
  yourRegistration: 'Your Registration',
  selectRegistration: 'Select a Registration',
  familySuffix: ' Family',
  schoolYearLabel: (year) => `School Year: ${year || '—'}`,
  totalLabel: (amount) => `Total: ${amount}`,
  paidLabel: (amount) => `Paid: ${amount}`,
  paidInFull: 'Paid in Full',
  balanceLabel: (amount) => `Balance: ${amount}`,
  paymentHistory: 'Payment History',
  date: 'Date',
  amount: 'Amount',
  method: 'Method',
  noPaymentsYet: 'No payments on file yet for this registration.',
  fullyPaid: 'Fully Paid',
  payBalanceButton: (amount) => `Pay ${amount} Balance`,

  // Pay step
  backToRegistration: 'Back to Registration',
  makePayment: 'Make Payment',
  balanceDue: (amount) => `Balance Due: ${amount}`,
  paymentAmountLabel: 'Payment Amount ($)',
  loadingPaymentForm: 'Loading payment form…',
  payButton: (amount) => `Pay ${amount}`,

  // Errors
  pleaseEnterEmail: 'Please enter your email address.',
  pleaseEnterValidEmail: 'Please enter a valid email address.',
  noRegistrationsFound: (email, year) =>
    `No registrations found for ${email}${year ? ` in ${year}` : ''}.`,
  lookupFailed: 'Unable to look up registrations. Please try again.',
  pleaseSelectRegistration: 'Please select a registration to pay.',
  noRemainingBalance: 'This registration has no remaining balance.',
  pleaseEnterValidAmount: 'Please enter a valid payment amount.',
  paymentExceedsBalance: (amount) => `Payment exceeds remaining balance of ${amount}.`,
  paymentFormNotReady: 'Payment form not ready. Please wait a moment and try again.',
  paymentSystemFailed: 'Payment system failed to load. Please refresh and try again.',
  cardDetailsInvalid: 'Card details are invalid.',
  paymentFailed: 'Payment failed. Please try again.',

  // Success
  paymentSuccess: (amount, balance) =>
    `Payment of ${amount} processed. New balance: ${balance}.`,

  // Locale for formatters
  locale: 'en-US'
};

const es = {
  languageLabel: { en: 'English', es: 'Español' },

  // Lookup step
  pageTitle: 'Pago de Educación Religiosa',
  pageSubtitle: 'Ingrese el correo electrónico utilizado en la inscripción y seleccione el año escolar que va a pagar.',
  schoolYear: 'Año escolar',
  noSchoolYears: 'No hay años escolares configurados',
  emailAddress: 'Correo electrónico',
  lookUpRegistration: 'Buscar inscripción',

  // Select step
  lookUpDifferentEmail: 'Buscar con otro correo',
  yourRegistration: 'Su inscripción',
  selectRegistration: 'Seleccione una inscripción',
  familySuffix: ' (Familia)',
  schoolYearLabel: (year) => `Año escolar: ${year || '—'}`,
  totalLabel: (amount) => `Total: ${amount}`,
  paidLabel: (amount) => `Pagado: ${amount}`,
  paidInFull: 'Pagado en su totalidad',
  balanceLabel: (amount) => `Saldo: ${amount}`,
  paymentHistory: 'Historial de pagos',
  date: 'Fecha',
  amount: 'Monto',
  method: 'Método',
  noPaymentsYet: 'Aún no hay pagos registrados para esta inscripción.',
  fullyPaid: 'Pagado completamente',
  payBalanceButton: (amount) => `Pagar saldo de ${amount}`,

  // Pay step
  backToRegistration: 'Volver a la inscripción',
  makePayment: 'Realizar pago',
  balanceDue: (amount) => `Saldo pendiente: ${amount}`,
  paymentAmountLabel: 'Monto del pago ($)',
  loadingPaymentForm: 'Cargando formulario de pago…',
  payButton: (amount) => `Pagar ${amount}`,

  // Errors
  pleaseEnterEmail: 'Por favor ingrese su correo electrónico.',
  pleaseEnterValidEmail: 'Por favor ingrese un correo electrónico válido.',
  noRegistrationsFound: (email, year) =>
    `No se encontraron inscripciones para ${email}${year ? ` en ${year}` : ''}.`,
  lookupFailed: 'No se pudo buscar las inscripciones. Por favor intente de nuevo.',
  pleaseSelectRegistration: 'Por favor seleccione una inscripción para pagar.',
  noRemainingBalance: 'Esta inscripción no tiene saldo pendiente.',
  pleaseEnterValidAmount: 'Por favor ingrese un monto de pago válido.',
  paymentExceedsBalance: (amount) => `El pago excede el saldo pendiente de ${amount}.`,
  paymentFormNotReady: 'El formulario de pago no está listo. Por favor espere un momento e intente de nuevo.',
  paymentSystemFailed: 'El sistema de pago no se pudo cargar. Por favor actualice la página e intente de nuevo.',
  cardDetailsInvalid: 'Los datos de la tarjeta no son válidos.',
  paymentFailed: 'El pago falló. Por favor intente de nuevo.',

  // Success
  paymentSuccess: (amount, balance) =>
    `Pago de ${amount} procesado. Nuevo saldo: ${balance}.`,

  // Locale for formatters
  locale: 'es-US'
};

export const translations = { en, es };
